import clsx from 'clsx';
import React from 'react';
import { useApiClients } from '../../client/api/useApiClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import Loader from '../Loader';
import LogoutIcon from '../../assets/icons/log-out.svg?react';
import { useLocalStorage } from '@uidotdev/usehooks';
import {
  LoginResponseData,
  ManualSubscriptionStatusEnum,
  ResponseCreateCheckoutSession,
  SubscriptionDetailsResponseDto,
} from '@EHSNavigator/backend';
import Button from '../Button';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { logout } from '../../actions/useAction';
import moment from 'moment';

interface PlanExpiredProps {
  otherClasses?: string;
}

const PlanExpired: React.FC<PlanExpiredProps> = ({ otherClasses }) => {
  const planExpiredClasses = clsx(
    otherClasses,
    'w-full relative min-h-dvh flex items-center justify-center bg-black px-4',
  );
  const [userInfo, setUserInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const { StripeApi, UserSettingsApi, OnboardOrganizationApi } = useApiClients();
  const dispatch = useDispatch();
  const { data = [], isLoading } = useQuery({
    queryKey: ['stripeProducts'],
    queryFn: async () => {
      const response = await StripeApi.stripeControllerGetProducts();
      return response?.data?.products || [];
    },
  });

  const { data: current, isLoading: currentLoading } = useQuery<
    SubscriptionDetailsResponseDto
  >({
    queryKey: ['get-subscription-details'],
    queryFn: async () => {
      const response =
        await UserSettingsApi.userSettingsControllerGetSubscriptionDetails({
          userId: userInfo?.user?.id as string,
        });
      return response?.data;
    },
  });

  const currentPlan = data?.find(
    (plan) =>
      plan.metadata?.plan?.toLowerCase() ===
      userInfo?.currentSubscription?.name.toLowerCase(),
  );

  const mutation = useMutation<ResponseCreateCheckoutSession, Error>({
    mutationFn: async () => {
      const response = await StripeApi?.stripeControllerCreateCheckoutSessionForCurrentSubscription();
      return response?.data;
    },
    onSuccess: (data) => {
      window.open(data?.session_url, '_blank');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });


  const requestAccessMutation = useMutation<{ message: string }, Error, { isExpired: boolean }>({
    mutationFn: async ({ isExpired = false }: { isExpired: boolean }) => {
      if (!userInfo?.user?.id) throw new Error('Token is missing');
      const response = await OnboardOrganizationApi.onboardOrganizationControllerRequestAccess({
        requestAccessDto: { uid: userInfo?.user?.id as string, requestType: isExpired ? ManualSubscriptionStatusEnum.RequestExtension : ManualSubscriptionStatusEnum.RequestAccess },
      });
      return response.data;
    },
    onSuccess: (data) => {
      handleLogout()
      toast.success(data?.message || 'Access request submitted successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      toast.error(errorMessage || 'An unexpected error occurred.');
    },
  });


  const handleLogout = async () => {
    await dispatch(logout(setUserInfo) as any);
  };

  if (isLoading || currentLoading) {
    return <Loader />;
  }

  return (
    <div className={planExpiredClasses} data-testid="plan-expired">
      <img
        src="/assets/images/background-image.png"
        alt="plan-expired"
        className="absolute top-0 left-0 hidden h-full w-full object-fill opacity-80 blur-xs md:block"
      />
      <img
        src="/assets/images/background-mobile-image.png"
        alt="plan-expired"
        className="absolute top-0 left-0 h-full w-full object-fill opacity-80 blur-xs md:hidden"
      />
      <div className="bg-translucent-background shadow-pricing-plans-card relative my-auto h-auto w-full max-w-573 overflow-auto rounded-2xl p-5 lg:px-9 lg:py-6">
        <button
          className="font-inter-variable global-transition text-brand-byzantine hover:text-brand-posh-pink flex w-fit cursor-pointer items-center !gap-1 rounded text-left text-base leading-6 font-medium"
          onClick={handleLogout}
        >
          <LogoutIcon className="h-5 w-5 [&>path]:fill-current" />
          Logout
        </button>
        <img
          src="/assets/images/logo.png"
          alt="logo"
          className="mx-auto mb-6 h-85 w-69 md:h-100 md:w-20"
        />

        {
          current?.subscription && (
            <>
              <h4 className="mb-4 text-center">
                Your Plan Has <span className="text-brand-posh-pink">Expired</span> -
                Renew to <span className="text-brand-byzantine">Continue</span>{' '}
                Enjoying Our Services
              </h4>
              <p className="font-inter-variable text-center text-base font-normal">
                We’ve loved having you with us! To keep your team’s access
                uninterrupted, simply reactivate your {currentPlan?.name} Plan below.
              </p>
              {current.subscription?.currentPlan?.subscription?.lastPaymentError && (
                <p className="border-brand-posh-pink bg-brand-posh-pink/20 text-brand-posh-pink font-inter-variable my-3 rounded-lg border px-4 py-1 text-center text-xs leading-4 font-medium capitalize sm:text-base sm:leading-5">
                  {current.subscription?.currentPlan?.subscription?.lastPaymentError}
                </p>
              )}
              <div className="bg-brand-byzantine rounded-10 mt-3 p-6">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-bold sm:text-base sm:leading-5">
                    CURRENT PLAN
                  </p>

                  <p className="bg-brand-posh-pink font-inter-variable rounded-lg px-4 py-1 text-xs leading-4 font-bold text-white capitalize sm:text-base sm:leading-5">
                    {current.subscription?.currentPlan?.subscription?.status}
                  </p>
                </div>
                <div className="border-translucent-background/65 mt-15 flex items-start justify-between gap-2.5 border-b pb-9">
                  <div className="max-w-191">
                    <h3 className="text-white capitalize">{currentPlan?.name}</h3>
                    <p className="font-inter-variable mt-1 text-sm font-normal text-white">
                      {currentPlan?.description}
                    </p>
                  </div>
                  <div>
                    <h1 className="text-white">
                      $
                      {currentPlan?.price?.unit_amount
                        ? currentPlan?.price?.unit_amount / 100
                        : 0}
                    </h1>
                    <p className="font-inter-variable text-sm font-bold text-white/80">
                      {currentPlan?.price?.nickname}, Per{' '}
                      {currentPlan?.price?.recurring?.interval}
                    </p>
                  </div>
                </div>

                <Button
                  variant="secondary"
                  otherClasses="mt-15 w-full"
                  size="smaller"
                  onClick={() => mutation.mutate()}
                  loading={mutation?.isPending}
                  disable={mutation?.isPending}
                >
                  Renew Now
                </Button>
              </div>
            </>
          )
        }

        {
          current?.manualSubscription && (
            <>
              <h4 className="mb-4 text-center">
                {(() => {
                  const { status } = current.manualSubscription;
                  switch (status) {
                    case ManualSubscriptionStatusEnum.Rejected:
                      return <>Your Organization Plan Has Been <span className="text-brand-posh-pink capitalize">{status}</span> - Please Check Below</>;
                    case ManualSubscriptionStatusEnum.Expired:
                      return <>Your Organization Plan Has <span className="text-brand-posh-pink capitalize">{status}</span> - Renew to <span className="text-brand-byzantine">Continue</span> Enjoying Services</>;
                    case ManualSubscriptionStatusEnum.Cancelled:
                      return <>Your Organization Plan Has Been <span className="text-brand-posh-pink capitalize">{status}</span> - Reactivate to Continue</>;
                    default:
                      return <>Your Organization Plan Status is Unknown</>;
                  }
                })()}
              </h4>
              <p className="font-inter-variable text-center text-base font-normal">
                {(() => {
                  const { status, rejectedMessage } = current.manualSubscription;
                  switch (status) {
                    case ManualSubscriptionStatusEnum.Rejected:
                      return rejectedMessage
                        ? `Unfortunately, your access request was rejected: ${rejectedMessage}`
                        : "Unfortunately, your access request was rejected by the platform owner.";
                    case ManualSubscriptionStatusEnum.Expired:
                      return "We’ve loved having you with us! Renew your plan below to continue enjoying our services.";
                    case ManualSubscriptionStatusEnum.Cancelled:
                      return "To keep your team’s access uninterrupted, simply reactivate your organization plan below.";
                    default:
                      return "Please contact support for more information.";
                  }
                })()}
              </p>

              <div className="bg-brand-byzantine rounded-10 mt-3 p-6">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-translucent-background/65 font-inter-variable text-xs leading-4 font-bold sm:text-base sm:leading-5">
                    Manual Subscription Plan
                  </p>

                  <p className="bg-brand-posh-pink font-inter-variable rounded-lg px-4 py-1 text-xs leading-4 font-bold text-white capitalize sm:text-base sm:leading-5">
                    {current.manualSubscription?.status}
                  </p>
                </div>
                <div className="border-translucent-background/65 mt-15 flex items-start justify-between gap-2.5 border-b pb-9">
                  <div className="max-w-191">
                    <h3 className="text-white capitalize">{"Join Now"}</h3>
                    <p className="font-inter-variable mt-1 text-sm font-normal text-white">
                      This plan was created on{' '}
                      {moment(current.manualSubscription.createdAt).format('MMMM D, YYYY')}{' '}
                      and will expire on{' '}
                      {moment(current.manualSubscription.expiresAt).format('MMMM D, YYYY')}.{' '}
                      It allows up to {current.manualSubscription.licenseCount} licenses for your team.
                    </p>
                  </div>
                  <div>
                    {/* <h1 className="text-white">
                      $
                      {currentPlan?.price?.unit_amount
                        ? currentPlan?.price?.unit_amount / 100
                        : 0}
                    </h1> */}
                    {/* <p className="font-inter-variable text-sm font-bold text-white/80">
                      {currentPlan?.price?.nickname}, Per{' '}
                      {currentPlan?.price?.recurring?.interval}
                    </p> */}
                  </div>
                </div>

                <Button
                  variant="secondary"
                  otherClasses="mt-15 w-full"
                  size="smaller"
                  onClick={() => requestAccessMutation.mutate({ isExpired: current?.manualSubscription?.status === ManualSubscriptionStatusEnum.Expired })}
                  loading={requestAccessMutation?.isPending}
                  disable={requestAccessMutation?.isPending}
                >

                  {(() => {
                    const { status } = current.manualSubscription;
                    switch (status) {
                      case ManualSubscriptionStatusEnum.Rejected:
                        return "Request for Access";
                      case ManualSubscriptionStatusEnum.Expired:
                        return "Renew";
                      case ManualSubscriptionStatusEnum.RejectedExtension:
                        return "Request for Extension";
                      case ManualSubscriptionStatusEnum.Cancelled:
                        return "Request for Access";
                      default:
                        return "Request for Access";
                    }
                  })()}
                  {/* Request for Access */}
                </Button>
              </div>
            </>
          )
        }

        {
          !current?.subscription && !current?.manualSubscription && (
            <>
            </>
          )
        }
      </div>
    </div>
  );
};

export default PlanExpired;
